import { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  type DocumentPhotoAttachment,
  type PersistedWorkItemAttachment,
  type PersistedDocumentAttachmentMetadata,
  type StructuredAttachmentPreview,
  type StructuredAttachmentPreviewResponse,
  type AttachmentUploadStatus,
  type DocumentAttachmentOcrResult,
  type DocumentAttachmentOcrLine,
  type DocumentFormState,
  DOCUMENT_ATTACHMENT_CATEGORY,
  MAX_ATTACHMENT_COUNT,
  MAX_ATTACHMENT_FILE_SIZE_BYTES,
  MAX_ATTACHMENT_TOTAL_BYTES,
  MAX_ATTACHMENT_ZIP_ENTRIES,
  MAX_ATTACHMENT_ZIP_TOTAL_BYTES,
  MAX_ATTACHMENT_ANALYSIS_CONCURRENCY,
  ATTACHMENT_ANALYSIS_TIMEOUT_MS,
  ATTACHMENT_PURCHASE_STRUCTURED_EXTENSIONS,
  ATTACHMENT_SALES_STRUCTURED_EXTENSIONS,
  ATTACHMENT_IMAGE_EXTENSIONS,
  OCR_SAFE_TOTAL_CONFIDENCE_THRESHOLD,
  SALES_CONFIRM_SIGNAL_CONFIDENCE_THRESHOLD,
  getAttachmentFileExtension,
  formatStatementDateValue,
  formatAttachmentFileSize,
  isImageLikeAttachment,
  parsePersistedDocumentAttachmentMetadata,
  buildAttachmentOcrResult,
  getAttachmentConfidence,
  shouldConfirmDetectedTotalApply,
  buildDetectedSignalLabel,
} from '../documentModalHelpers';
import { roundMoney } from '@/lib/money';
import { authService } from '@/lib/authService';
import { formatLocalISOSAST, getDateStrSAST } from '@/lib/sastTime';
import {
  analyzeReceiptImages,
  buildReceiptAmountValidation,
  getDefaultReceiptCaptureProfile,
  type ReceiptAiPayload,
  type ReceiptCaptureProfile,
  type POAnalysisContext,
} from '@/lib/receiptAiAnalysis';
import { uploadToCompanyDrive } from '@/lib/companyDriveUpload';
import {
  buildSalesIntakePreview,
  type SalesIntakePreview,
} from '@/lib/finance/salesIntakeAnalysis';
import { confirm } from '@/lib/notifications';
import type { Document } from '@/hooks/useDocumentsWorkItems';

interface UseAttachmentProcessingParams {
  isPurchaseDocument: boolean;
  formData: DocumentFormState;
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormState>>;
  document: Document | null;
  formatDateForInput: (dateValue: string | Date | null | undefined) => string;
  /**
   * Called after a receipt photo is re-analyzed via Gemini Vision.
   * The caller can use the receipt result to re-extract line items,
   * update vendor/date fields, etc.
   */
  onReanalyzeComplete?: (receipt: ReceiptAiPayload) => void;
  /** Document type for capture profile selection (e.g. 'din', 'expense', 'supplier_invoice') */
  entryType?: string | null;
  /** Selected supplier context for delivery note / quote validation */
  supplierContext?: {
    supplier_id: string | number;
    supplier_name: string;
    is_delivery_note: boolean;
  } | null;
  /** PO context for supplier invoice / delivery note analysis */
  poContext?: POAnalysisContext | null;
}

const RECEIPT_ATTACHMENT_CATEGORIES = [DOCUMENT_ATTACHMENT_CATEGORY, 'receipt', 'document'];

export function useAttachmentProcessing({
  isPurchaseDocument,
  formData,
  setFormData,
  document: doc,
  formatDateForInput,
  onReanalyzeComplete,
  entryType,
  supplierContext,
  poContext,
}: UseAttachmentProcessingParams) {
  const [photoAttachments, setPhotoAttachments] = useState<DocumentPhotoAttachment[]>([]);
  const [loadingPersistedAttachments, setLoadingPersistedAttachments] = useState(false);
  const [isAttachmentDragActive, setIsAttachmentDragActive] = useState(false);

  const ocrProcessing = useMemo(
    () => photoAttachments.some((attachment) => attachment.ocr_processing),
    [photoAttachments],
  );

  const currentDocumentId = useMemo(() => {
    const rawId = doc?.id ?? formData.id;
    if (rawId == null || String(rawId).trim() === '') {
      return null;
    }
    return String(rawId);
  }, [doc?.id, formData.id]);

  const buildAuthorizedHeaders = useCallback((extraHeaders?: Record<string, string>) => {
    const token = authService.getTokens()?.access_token;
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extraHeaders,
    };
  }, []);

  const buildPersistedAttachmentMetadata = useCallback((
    attachment: DocumentPhotoAttachment,
    persistedVia: 'auto_save' | 'manual_save',
  ): PersistedDocumentAttachmentMetadata => ({
    source: 'document_modal',
    version: 1,
    file_name: attachment.file_name,
    file_type: attachment.file_type,
    mime_type: attachment.mime_type,
    persisted_at: formatLocalISOSAST(new Date()),
    persisted_via: persistedVia,
    analysis_error: attachment.analysis_error,
    ocr_result: attachment.ocr_result,
    sales_result: attachment.sales_result,
    structured_result: attachment.structured_result,
  }), []);

  const mapPersistedAttachmentToState = useCallback((attachment: PersistedWorkItemAttachment): DocumentPhotoAttachment | null => {
    const metadata = parsePersistedDocumentAttachmentMetadata(attachment.metadata);
    if (!RECEIPT_ATTACHMENT_CATEGORIES.includes(attachment.category ?? '') && !metadata) {
      return null;
    }

    const fileName = attachment.file_name || metadata?.file_name || 'Attachment';
    const fileType = attachment.file_type || metadata?.file_type || getAttachmentFileExtension(fileName) || 'file';
    const mimeType = attachment.mime_type || metadata?.mime_type || 'application/octet-stream';

    return {
      id: `persisted-${attachment.id}`,
      attachment_id: String(attachment.id),
      work_item_id: attachment.work_item_id ? String(attachment.work_item_id) : undefined,
      file_name: fileName,
      file_type: fileType,
      mime_type: mimeType,
      file_size: attachment.file_size == null ? undefined : Number(attachment.file_size),
      file_path: attachment.file_path || undefined,
      ocr_result: metadata?.ocr_result,
      sales_result: metadata?.sales_result,
      structured_result: metadata?.structured_result,
      analysis_error: metadata?.analysis_error,
      ocr_processing: false,
      upload_status: 'uploaded',
      is_persisted: true,
      sync_required: false,
      category: attachment.category || DOCUMENT_ATTACHMENT_CATEGORY,
    };
  }, []);

  const readBlobAsDataUrl = useCallback((blob: Blob, fileName: string) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result;
      if (typeof result !== 'string' || result.length === 0) {
        reject(new Error(`Could not read ${fileName}`));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error(`Could not read ${fileName}`));
    reader.readAsDataURL(blob);
  }), []);

  const ensureAttachmentFile = useCallback(async (attachment: DocumentPhotoAttachment): Promise<File> => {
    if (attachment.file_blob) {
      return attachment.file_blob;
    }

    if (!attachment.file_path) {
      throw new Error('The original file is not available for re-analysis.');
    }

    // Route persisted attachment downloads through the Next.js same-origin proxy
    // to avoid cross-origin CORS issues with the cloud-storage service.
    // The proxy authenticates the user and fetches from the internal cloud-storage URL.
    let fetchUrl: string = attachment.file_path;
    if (attachment.attachment_id && attachment.work_item_id) {
      fetchUrl = `/api/v1/work-items/${attachment.work_item_id}/attachments/${attachment.attachment_id}/download`;
    }

    const response = await fetch(fetchUrl, { credentials: 'include' });
    if (!response.ok) {
      throw new Error(`Could not load ${attachment.file_name} for re-analysis.`);
    }

    const blob = await response.blob();
    return new File([blob], attachment.file_name, {
      type: attachment.mime_type || blob.type || 'application/octet-stream',
    });
  }, []);

  const resolveAttachmentImageDataUrl = useCallback(async (attachment: DocumentPhotoAttachment): Promise<string> => {
    if (attachment.data_url?.startsWith('data:image/')) {
      return attachment.data_url;
    }

    const file = await ensureAttachmentFile(attachment);
    // Re-type the blob as an image so FileReader.readAsDataURL produces a
    // data:image/... URL instead of data:application/octet-stream;base64,...
    // (which happens when the proxy/cloud-storage returns a generic MIME type).
    const imageMimeType = isImageLikeAttachment(attachment.file_name, attachment.mime_type)
      ? attachment.mime_type || 'image/jpeg'
      : 'image/jpeg';
    const imageBlob = file.type.startsWith('image/')
      ? file
      : new Blob([file], { type: imageMimeType });
    return readBlobAsDataUrl(imageBlob, attachment.file_name);
  }, [ensureAttachmentFile, readBlobAsDataUrl]);

  const fetchAttachmentJson = useCallback(async <TData,>(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<TData> => {
    const response = await fetch(input, {
      credentials: 'include',
      ...init,
    });

    const payload = await response.json().catch(() => ({} as { error?: string; data?: TData; success?: boolean }));
    if (!response.ok || payload.success === false) {
      throw new Error(typeof payload.error === 'string' ? payload.error : 'Attachment request failed');
    }

    return (payload.data as TData) ?? (payload as TData);
  }, []);

  const loadPersistedDocumentAttachments = useCallback(async (
    workItemId: string,
    fallbackReceiptUrl?: string | null,
  ) => {
    setLoadingPersistedAttachments(true);
    try {
      const attachments = await fetchAttachmentJson<PersistedWorkItemAttachment[]>(
        `/api/v1/work-items/${workItemId}/attachments`,
        {
          headers: buildAuthorizedHeaders(),
        },
      );

      const mappedAttachments = attachments
        .map((attachment) => mapPersistedAttachmentToState(attachment))
        .filter((attachment): attachment is DocumentPhotoAttachment => Boolean(attachment));

      if (mappedAttachments.length === 0 && fallbackReceiptUrl) {
        const fileName = fallbackReceiptUrl.split('/').pop()?.split('?')[0] || 'receipt';
        const isPdf = /\.pdf($|\?)/i.test(fallbackReceiptUrl);
        setPhotoAttachments([{
          id: `receipt-url-${workItemId}`,
          file_name: fileName,
          file_type: isPdf ? 'pdf' : (getAttachmentFileExtension(fileName) || 'file'),
          mime_type: isPdf ? 'application/pdf' : 'image/jpeg',
          file_path: fallbackReceiptUrl,
          file_size: 0,
          ocr_processing: false,
          upload_status: 'uploaded',
          is_persisted: true,
          sync_required: false,
        }]);
      } else {
        setPhotoAttachments(mappedAttachments);
      }
    } catch (error) {
      console.error('[DocumentModal] Failed to load persisted attachments:', error);
      if (fallbackReceiptUrl) {
        const fileName = fallbackReceiptUrl.split('/').pop()?.split('?')[0] || 'receipt';
        const isPdf = /\.pdf($|\?)/i.test(fallbackReceiptUrl);
        setPhotoAttachments([{
          id: `receipt-url-${workItemId}`,
          file_name: fileName,
          file_type: isPdf ? 'pdf' : (getAttachmentFileExtension(fileName) || 'file'),
          mime_type: isPdf ? 'application/pdf' : 'image/jpeg',
          file_path: fallbackReceiptUrl,
          file_size: 0,
          ocr_processing: false,
          upload_status: 'uploaded',
          is_persisted: true,
          sync_required: false,
        }]);
      } else {
        setPhotoAttachments([]);
      }
    } finally {
      setLoadingPersistedAttachments(false);
    }
  }, [buildAuthorizedHeaders, fetchAttachmentJson, mapPersistedAttachmentToState]);

  const syncPhotoAttachments = useCallback(async (
    workItemId: string,
    persistedVia: 'auto_save' | 'manual_save',
  ) => {
    const attachmentsToCreate = photoAttachments.filter((attachment) => (
      Boolean(attachment.file_blob)
      && !attachment.is_persisted
      && attachment.upload_status !== 'uploading'
    ));

    const attachmentsToUpdate = photoAttachments.filter((attachment) => (
      attachment.is_persisted
      && Boolean(attachment.attachment_id)
      && attachment.sync_required
      && attachment.upload_status === 'uploaded'
    ));

    let uploadedCount = 0;
    let updatedCount = 0;
    let failedCount = 0;

    for (const attachment of attachmentsToCreate) {
      setPhotoAttachments((prev) => prev.map((photo) => (
        photo.id === attachment.id
          ? { ...photo, upload_status: 'uploading', upload_error: undefined }
          : photo
      )));

      try {
        const uploadResult = await uploadToCompanyDrive(
          attachment.file_blob!,
          `finance-document/${workItemId}`,
        );

        if (!uploadResult.success || !(uploadResult.directUrl || uploadResult.url)) {
          throw new Error(uploadResult.error || `Could not upload ${attachment.file_name}`);
        }

        const filePath = uploadResult.directUrl || uploadResult.url || '';
        const createdAttachment = await fetchAttachmentJson<PersistedWorkItemAttachment>(
          `/api/v1/work-items/${workItemId}/attachments`,
          {
            method: 'POST',
            headers: buildAuthorizedHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              file_name: attachment.file_name,
              file_path: filePath,
              file_size: attachment.file_blob?.size ?? attachment.file_size ?? 0,
              file_type: attachment.file_type,
              mime_type: attachment.mime_type,
              category: DOCUMENT_ATTACHMENT_CATEGORY,
              metadata: buildPersistedAttachmentMetadata(attachment, persistedVia),
            }),
          },
        );

        uploadedCount += 1;
        setPhotoAttachments((prev) => prev.map((photo) => (
          photo.id === attachment.id
            ? {
              ...photo,
              attachment_id: String(createdAttachment.id),
              work_item_id: workItemId,
              file_path: createdAttachment.file_path || filePath,
              upload_status: 'uploaded',
              is_persisted: true,
              sync_required: false,
              upload_error: undefined,
              category: createdAttachment.category || DOCUMENT_ATTACHMENT_CATEGORY,
            }
            : photo
        )));
      } catch (error) {
        failedCount += 1;
        const message = error instanceof Error ? error.message : `Could not save ${attachment.file_name}`;
        setPhotoAttachments((prev) => prev.map((photo) => (
          photo.id === attachment.id
            ? { ...photo, upload_status: 'failed', upload_error: message }
            : photo
        )));
      }
    }

    for (const attachment of attachmentsToUpdate) {
      try {
        await fetchAttachmentJson<PersistedWorkItemAttachment>(
          `/api/v1/work-items/${workItemId}/attachments/${attachment.attachment_id}`,
          {
            method: 'PATCH',
            headers: buildAuthorizedHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              category: DOCUMENT_ATTACHMENT_CATEGORY,
              metadata: buildPersistedAttachmentMetadata(attachment, persistedVia),
            }),
          },
        );

        updatedCount += 1;
        setPhotoAttachments((prev) => prev.map((photo) => (
          photo.id === attachment.id
            ? { ...photo, sync_required: false, upload_error: undefined }
            : photo
        )));
      } catch (error) {
        failedCount += 1;
        const message = error instanceof Error ? error.message : `Could not update ${attachment.file_name}`;
        setPhotoAttachments((prev) => prev.map((photo) => (
          photo.id === attachment.id
            ? { ...photo, upload_status: 'failed', upload_error: message }
            : photo
        )));
      }
    }

    return { uploadedCount, updatedCount, failedCount };
  }, [buildAuthorizedHeaders, buildPersistedAttachmentMetadata, fetchAttachmentJson, photoAttachments]);

  const fetchWithAttachmentTimeout = useCallback(async (
    input: RequestInfo | URL,
    init: RequestInit,
  ) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), ATTACHMENT_ANALYSIS_TIMEOUT_MS);

    try {
      return await fetch(input, {
        ...init,
        signal: controller.signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('Analysis timed out. Please try a smaller file or split the upload.');
      }

      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }, []);

  const runOcrAnalysisMulti = useCallback(async (
    imageDataUrls: string[],
    photoIds: string[],
  ) => {
    if (imageDataUrls.length === 0) return;
    const effectiveEntryType = entryType || (isPurchaseDocument ? 'purchase' : 'sales');
    const captureProfile = getDefaultReceiptCaptureProfile(effectiveEntryType);
    const declaredAmount = Number(formData.grand_total);
    const normalizedDeclaredAmount = Number.isFinite(declaredAmount) && declaredAmount > 0
      ? declaredAmount
      : undefined;

    // Mark all photos as processing
    setPhotoAttachments((prev) => prev.map((photo) => (
      photoIds.includes(photo.id)
        ? {
          ...photo,
          ocr_processing: true,
          analysis_error: undefined,
          structured_result: undefined,
          sales_result: undefined,
        }
        : photo
    )));

    try {
      const analyzed = await analyzeReceiptImages({
        images: imageDataUrls,
        entry_type: effectiveEntryType,
        declared_amount: normalizedDeclaredAmount,
        capture_profile: captureProfile,
        po_context: poContext ?? undefined,
        supplier_context: supplierContext ?? undefined,
      });

      if (analyzed.success && analyzed.receipt) {
        const receipt = analyzed.receipt;
        const validation = analyzed.validation ?? buildReceiptAmountValidation(
          normalizedDeclaredAmount ?? null,
          receipt.total_amount ?? null,
        );
        const ocrSnapshot = buildAttachmentOcrResult(receipt, validation);
        const attachmentName = photoAttachments.find((p) => photoIds.includes(p.id))?.file_name;
        const salesPreview = !isPurchaseDocument
          ? buildSalesIntakePreview({
            fileName: attachmentName,
            extractedText: receipt.raw_text,
            lineItems: (receipt.line_items || []).map((lineItem) => ({
              description: lineItem.description,
              quantity: lineItem.quantity,
              unit_price: lineItem.unit_price,
              amount: lineItem.total,
            })),
            totalAmount: receipt.total_amount,
            documentDate: receipt.receipt_date,
            counterpartyName: receipt.vendor_name,
            currency: receipt.currency,
          })
          : undefined;

        // Apply the result to the first photo (the primary one)
        setPhotoAttachments((prev) => prev.map((photo) => (
          photo.id === photoIds[0]
            ? {
              ...photo,
              ocr_result: ocrSnapshot,
              sales_result: salesPreview,
              ocr_processing: false,
              analysis_error: undefined,
              sync_required: true,
            }
            : photoIds.includes(photo.id)
              ? { ...photo, ocr_processing: false, sync_required: true }
              : photo
        )));

        if (isPurchaseDocument && receipt.vendor_name) {
          toast.success(`Receipt detected vendor: ${receipt.vendor_name}`);
        } else if (!isPurchaseDocument && salesPreview?.customer_po_number) {
          toast.success(`Detected customer PO: ${salesPreview.customer_po_number}`);
        }

        if (isPurchaseDocument && receipt.total_amount != null) {
          const validationNote = validation?.message ? ` ${validation.message}` : '';
          toast.success(`Detected total ready to review. ${buildDetectedSignalLabel(ocrSnapshot.confidence ?? null)}.${validationNote}`.trim());
        }

        onReanalyzeComplete?.(receipt);
      } else {
        setPhotoAttachments((prev) => prev.map((photo) => (
          photoIds.includes(photo.id)
            ? { ...photo, ocr_processing: false }
            : photo
        )));
      }
    } catch (error) {
      console.error('[DocumentModal] Multi-image receipt analysis error:', error);
      const message = isPurchaseDocument
        ? 'Receipt analysis failed. You can continue manually.'
        : 'Sales document analysis failed. You can continue manually.';
      setPhotoAttachments((prev) => prev.map((photo) => (
        photoIds.includes(photo.id)
          ? { ...photo, ocr_processing: false, analysis_error: message, sync_required: true }
          : photo
      )));
      toast.error(message);
    }
  }, [entryType, isPurchaseDocument, formData.grand_total, poContext, supplierContext, photoAttachments, onReanalyzeComplete]);

  const runOcrAnalysis = useCallback(async (imageDataUrl: string, photoId: string) => {
    try {
      const attachmentName = photoAttachments.find((photo) => photo.id === photoId)?.file_name;
      const declaredAmount = Number(formData.grand_total);
      const normalizedDeclaredAmount = Number.isFinite(declaredAmount) && declaredAmount > 0
        ? declaredAmount
        : undefined;

      setPhotoAttachments((prev) => prev.map((photo) => (
        photo.id === photoId
          ? {
            ...photo,
            ocr_processing: true,
            analysis_error: undefined,
            structured_result: undefined,
            sales_result: undefined,
          }
          : photo
      )));

      const analyzed = await analyzeReceiptImages({
        images: [imageDataUrl],
        entry_type: isPurchaseDocument ? 'purchase' : 'sales',
        declared_amount: normalizedDeclaredAmount,
      });

      if (analyzed.success && analyzed.receipt) {
        const receipt = analyzed.receipt;
        const validation = analyzed.validation ?? buildReceiptAmountValidation(
          normalizedDeclaredAmount ?? null,
          receipt.total_amount ?? null,
        );
        const ocrSnapshot = buildAttachmentOcrResult(receipt, validation);
        const salesPreview = !isPurchaseDocument
          ? buildSalesIntakePreview({
            fileName: attachmentName,
            extractedText: receipt.raw_text,
            lineItems: (receipt.line_items || []).map((lineItem) => ({
              description: lineItem.description,
              quantity: lineItem.quantity,
              unit_price: lineItem.unit_price,
              amount: lineItem.total,
            })),
            totalAmount: receipt.total_amount,
            documentDate: receipt.receipt_date,
            counterpartyName: receipt.vendor_name,
            currency: receipt.currency,
          })
          : undefined;

        setPhotoAttachments((prev) => prev.map((photo) => (
          photo.id === photoId
            ? {
              ...photo,
              ocr_result: ocrSnapshot,
              sales_result: salesPreview,
              ocr_processing: false,
              analysis_error: undefined,
              sync_required: true,
            }
            : photo
        )));

        if (isPurchaseDocument && receipt.vendor_name) {
          toast.success(`Receipt detected vendor: ${receipt.vendor_name}`);
        } else if (!isPurchaseDocument && salesPreview?.customer_po_number) {
          toast.success(`Detected customer PO: ${salesPreview.customer_po_number}`);
        }

        if (isPurchaseDocument && receipt.total_amount != null) {
          const validationNote = validation?.message ? ` ${validation.message}` : '';
          toast.success(`Detected total ready to review. ${buildDetectedSignalLabel(ocrSnapshot.confidence ?? null)}.${validationNote}`.trim());
        }

        // Notify the caller that a receipt was (re-)analyzed so they can
        // re-extract line items, update vendor/date, etc.
        onReanalyzeComplete?.(receipt);
      } else {
        setPhotoAttachments((prev) => prev.map((photo) => (
          photo.id === photoId
            ? { ...photo, ocr_processing: false }
            : photo
        )));
      }
    } catch (error) {
      console.error('[DocumentModal] Receipt analysis error:', error);
      const message = isPurchaseDocument
        ? 'Receipt analysis failed. You can continue manually.'
        : 'Sales document analysis failed. You can continue manually.';
      setPhotoAttachments((prev) => prev.map((photo) => (
        photo.id === photoId
          ? {
            ...photo,
            ocr_processing: false,
            analysis_error: message,
            sync_required: true,
          }
          : photo
      )));
      toast.error(message);
    }
  }, [formData.grand_total, isPurchaseDocument, photoAttachments, onReanalyzeComplete]);

  const previewSalesIntake = useCallback(async ({
    fileName,
    mimeType,
    fileContent,
    fileBase64,
  }: {
    fileName: string;
    mimeType?: string;
    fileContent?: string;
    fileBase64?: string;
  }): Promise<SalesIntakePreview> => {
    const response = await fetchWithAttachmentTimeout('/api/v1/finance/sales-intake', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_name: fileName,
        mime_type: mimeType,
        file_content: fileContent,
        file_base64: fileBase64,
      }),
    });

    const json = await response.json();
    if (!response.ok || !json.success) {
      throw new Error(json.error || 'Sales file analysis failed');
    }

    return json.data as SalesIntakePreview;
  }, [fetchWithAttachmentTimeout]);

  const previewStructuredStatement = useCallback(async ({
    fileName,
    fileContent,
    pdfBase64,
  }: {
    fileName: string;
    fileContent?: string;
    pdfBase64?: string;
  }): Promise<StructuredAttachmentPreview> => {
    const response = await fetchWithAttachmentTimeout('/api/v1/bank', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'previewStatement',
        file_name: fileName,
        file_content: fileContent,
        pdf_base64: pdfBase64,
      }),
    });

    const json = await response.json();
    if (!response.ok || !json.success) {
      throw new Error(json.error || 'File analysis failed');
    }

    const data = json.data as StructuredAttachmentPreviewResponse;

    return {
      bank: data.bank || undefined,
      format: data.format || getAttachmentFileExtension(fileName).toUpperCase() || 'FILE',
      total_rows: Number(data.total_rows || 0),
      parsed_rows: Number(data.parsed_rows || 0),
      duplicate_rows: Number(data.duplicate_rows || 0),
      start_date: formatStatementDateValue(data.start_date),
      end_date: formatStatementDateValue(data.end_date),
      opening_balance: data.opening_balance == null ? null : Number(data.opening_balance),
      closing_balance: data.closing_balance == null ? null : Number(data.closing_balance),
      warnings: [...(data.warnings || []), ...(data.errors || [])],
      rows: (data.transactions || []).map((transaction) => ({
        date: formatStatementDateValue(transaction.date) || String(transaction.date || ''),
        description: transaction.description,
        reference: transaction.reference || '',
        amount: Number(transaction.amount || 0),
        type: transaction.transaction_type,
        balance: transaction.balance == null ? null : Number(transaction.balance),
      })),
    };
  }, [fetchWithAttachmentTimeout]);

  const runStructuredFileAnalysis = useCallback(async (file: File, photoId: string) => {
    try {
      setPhotoAttachments((prev) => prev.map((photo) => (
        photo.id === photoId
          ? {
            ...photo,
            ocr_processing: true,
            analysis_error: undefined,
            ocr_result: undefined,
            structured_result: undefined,
            sales_result: undefined,
          }
          : photo
      )));

      const extension = getAttachmentFileExtension(file.name);
      const readFileBase64 = () => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result as string;
          const base64 = result?.split(',')[1];
          if (!base64) {
            reject(new Error(`Could not read ${file.name}`));
            return;
          }
          resolve(base64);
        };
        reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
        reader.readAsDataURL(file);
      });

      if (isPurchaseDocument) {
        let preview: StructuredAttachmentPreview;

        if (extension === 'pdf') {
          preview = await previewStructuredStatement({
            fileName: file.name,
            pdfBase64: await readFileBase64(),
          });
        } else if (extension === 'xlsx' || extension === 'xls') {
          const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          if (!firstSheetName) {
            throw new Error('No worksheet found in spreadsheet');
          }

          const csvContent = XLSX.utils.sheet_to_csv(workbook.Sheets[firstSheetName], { blankrows: false });
          if (!csvContent.trim()) {
            throw new Error('The spreadsheet is empty');
          }

          preview = await previewStructuredStatement({
            fileName: file.name.replace(/\.(xlsx|xls)$/i, '.csv'),
            fileContent: csvContent,
          });
        } else {
          const fileContent = await file.text();
          if (!fileContent.trim()) {
            throw new Error('The file is empty');
          }

          preview = await previewStructuredStatement({
            fileName: file.name,
            fileContent,
          });
        }

        const hasParsedContent = preview.parsed_rows > 0 || preview.rows.length > 0;
        setPhotoAttachments((prev) => prev.map((photo) => (
          photo.id === photoId
            ? {
              ...photo,
              ocr_processing: false,
              structured_result: preview,
              analysis_error: hasParsedContent ? undefined : (preview.warnings[0] || 'No structured rows were detected in this file.'),
              sync_required: true,
            }
            : photo
        )));

        if (!hasParsedContent) {
          toast.error(`No structured rows were detected in ${file.name}.`);
        }
        return;
      }

      let preview: SalesIntakePreview;

      if (extension === 'xlsx' || extension === 'xls') {
        const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          throw new Error('No worksheet found in spreadsheet');
        }

        const csvContent = XLSX.utils.sheet_to_csv(workbook.Sheets[firstSheetName], { blankrows: false });
        if (!csvContent.trim()) {
          throw new Error('The spreadsheet is empty');
        }

        preview = await previewSalesIntake({
          fileName: file.name.replace(/\.(xlsx|xls)$/i, '.csv'),
          mimeType: 'text/csv',
          fileContent: csvContent,
        });
      } else if (extension === 'pdf' || extension === 'docx') {
        preview = await previewSalesIntake({
          fileName: file.name,
          mimeType: file.type,
          fileBase64: await readFileBase64(),
        });
      } else {
        const fileContent = await file.text();
        if (!fileContent.trim()) {
          throw new Error('The file is empty');
        }

        preview = await previewSalesIntake({
          fileName: file.name,
          mimeType: file.type,
          fileContent,
        });
      }

      const hasParsedContent = Boolean(
        preview.customer_po_number
        || preview.customer_name
        || preview.requested_date
        || preview.total_amount != null
        || preview.lines.length > 0,
      );

      setPhotoAttachments((prev) => prev.map((photo) => (
        photo.id === photoId
          ? {
            ...photo,
            ocr_processing: false,
            sales_result: preview,
            analysis_error: hasParsedContent ? undefined : (preview.warnings[0] || 'No sales context was detected in this file.'),
            sync_required: true,
          }
          : photo
      )));

      if (!hasParsedContent) {
        toast.error(`No sales context was detected in ${file.name}.`);
      }
    } catch (error) {
      console.error('[DocumentModal] Structured file analysis error:', error);
      const message = error instanceof Error
        ? error.message
        : (isPurchaseDocument ? 'File analysis failed. You can continue manually.' : 'Sales file analysis failed. You can continue manually.');
      setPhotoAttachments((prev) => prev.map((photo) => (
        photo.id === photoId
          ? { ...photo, ocr_processing: false, analysis_error: message, sync_required: true }
          : photo
      )));
      toast.error(message);
    }
  }, [isPurchaseDocument, previewSalesIntake, previewStructuredStatement]);

  const processPhotoFiles = useCallback(async (incomingFiles: File[] | FileList | null | undefined) => {
    const files = Array.from(incomingFiles ?? []);
    if (files.length === 0) {
      return;
    }

    const supportedStructuredExtensions = isPurchaseDocument
      ? ATTACHMENT_PURCHASE_STRUCTURED_EXTENSIONS
      : ATTACHMENT_SALES_STRUCTURED_EXTENSIONS;
    const createAttachmentId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const existingTotalBytes = photoAttachments.reduce((total, attachment) => {
      return total + Number(attachment.file_size ?? attachment.file_blob?.size ?? 0);
    }, 0);
    let runningTotalBytes = existingTotalBytes;
    let remainingSlots = Math.max(MAX_ATTACHMENT_COUNT - photoAttachments.length, 0);
    let queuedFiles = 0;
    const rejectionMessages: string[] = [];
    const pendingAnalyses: Array<
      | { attachment: DocumentPhotoAttachment; kind: 'image'; dataUrl: string }
      | { attachment: DocumentPhotoAttachment; kind: 'file'; file: File }
    > = [];
    const supportedZipExtensions = new Set([
      ...Array.from(supportedStructuredExtensions),
      ...Array.from(ATTACHMENT_IMAGE_EXTENSIONS),
    ]);

    const addRejection = (message: string) => {
      rejectionMessages.push(message);
    };

    const inferMimeTypeFromExtension = (extension: string): string => {
      switch (extension) {
        case 'pdf':
          return 'application/pdf';
        case 'xlsx':
          return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'xls':
          return 'application/vnd.ms-excel';
        case 'docx':
          return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        case 'csv':
          return 'text/csv';
        case 'json':
          return 'application/json';
        case 'xml':
          return 'application/xml';
        case 'yaml':
        case 'yml':
          return 'application/yaml';
        case 'html':
          return 'text/html';
        case 'txt':
        case 'log':
        case 'md':
        default:
          return ATTACHMENT_IMAGE_EXTENSIONS.has(extension)
            ? `image/${extension === 'jpg' ? 'jpeg' : extension}`
            : 'text/plain';
      }
    };

    const expandSupportedFiles = async (file: File): Promise<File[]> => {
      const extension = getAttachmentFileExtension(file.name);
      if (extension !== 'zip') {
        return [file];
      }

      try {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(await file.arrayBuffer());
        const entries = Object.values(zip.files).filter((entry) => !entry.dir);

        if (entries.length > MAX_ATTACHMENT_ZIP_ENTRIES) {
          addRejection(`${file.name} contains ${entries.length} files. ZIP uploads are limited to ${MAX_ATTACHMENT_ZIP_ENTRIES} files.`);
          return [];
        }

        const extractedFiles: File[] = [];
        let extractedBytes = 0;

        for (const entry of entries) {
          const entryName = entry.name.split('/').pop() || entry.name;
          const entryExtension = getAttachmentFileExtension(entryName);
          if (!supportedZipExtensions.has(entryExtension)) {
            continue;
          }

          const data = await entry.async('uint8array');
          extractedBytes += data.byteLength;
          if (extractedBytes > MAX_ATTACHMENT_ZIP_TOTAL_BYTES) {
            addRejection(`${file.name} expands beyond ${formatAttachmentFileSize(MAX_ATTACHMENT_ZIP_TOTAL_BYTES)} and was skipped.`);
            return [];
          }

          const buffer = new ArrayBuffer(data.byteLength);
          new Uint8Array(buffer).set(data);
          extractedFiles.push(new File([buffer], entryName, { type: inferMimeTypeFromExtension(entryExtension) }));
        }

        if (extractedFiles.length === 0) {
          addRejection(`${file.name} did not contain any supported files.`);
        }

        return extractedFiles;
      } catch (error) {
        addRejection(error instanceof Error ? error.message : `Could not read ${file.name}`);
        return [];
      }
    };

    const candidateFiles: File[] = [];
    for (const file of files) {
      const expandedFiles = await expandSupportedFiles(file);
      candidateFiles.push(...expandedFiles);
    }

    for (const file of candidateFiles) {
      const extension = getAttachmentFileExtension(file.name);
      const isImage = isImageLikeAttachment(file.name, file.type);
      const isSupported = isImage || supportedStructuredExtensions.has(extension);

      if (!isSupported) {
        addRejection(
          isPurchaseDocument
            ? `${file.name} is not supported here yet. Use images, PDF, CSV, OFX, QFX, QIF, MT940, TXT, ZIP, XLSX, or XLS.`
            : `${file.name} is not supported here yet. Use images, PDF, CSV, TXT, ZIP, XLSX, XLS, DOCX, JSON, XML, Markdown, YAML, HTML, or LOG files.`,
        );
        continue;
      }

      if (file.size > MAX_ATTACHMENT_FILE_SIZE_BYTES) {
        addRejection(`${file.name} is ${formatAttachmentFileSize(file.size)}. Files are limited to ${formatAttachmentFileSize(MAX_ATTACHMENT_FILE_SIZE_BYTES)} each.`);
        continue;
      }

      if (remainingSlots <= 0) {
        addRejection(`You can attach up to ${MAX_ATTACHMENT_COUNT} files per document.`);
        break;
      }

      if (runningTotalBytes + file.size > MAX_ATTACHMENT_TOTAL_BYTES) {
        addRejection(`Adding ${file.name} would exceed the ${formatAttachmentFileSize(MAX_ATTACHMENT_TOTAL_BYTES)} document attachment budget.`);
        continue;
      }

      const attachment: DocumentPhotoAttachment = {
        id: createAttachmentId(),
        file_blob: file,
        file_name: file.name,
        file_type: extension || (isImage ? 'image' : 'file'),
        mime_type: file.type || inferMimeTypeFromExtension(extension),
        file_size: file.size,
        ocr_processing: true,
        upload_status: 'staged',
        is_persisted: false,
        sync_required: true,
        category: DOCUMENT_ATTACHMENT_CATEGORY,
      };

      if (isImage) {
        const dataUrl = await readBlobAsDataUrl(file, file.name);
        pendingAnalyses.push({
          attachment: { ...attachment, data_url: dataUrl },
          kind: 'image',
          dataUrl,
        });
      } else {
        pendingAnalyses.push({ attachment, kind: 'file', file });
      }

      queuedFiles += 1;
      remainingSlots -= 1;
      runningTotalBytes += file.size;
    }

    if (pendingAnalyses.length > 0) {
      setPhotoAttachments((prev) => [...prev, ...pendingAnalyses.map((entry) => entry.attachment)]);

      const queue = [...pendingAnalyses];
      let queueIndex = 0;
      const worker = async () => {
        while (queueIndex < queue.length) {
          const nextEntry = queue[queueIndex];
          queueIndex += 1;
          if (!nextEntry) {
            return;
          }

          if (nextEntry.kind === 'image') {
            await runOcrAnalysis(nextEntry.dataUrl, nextEntry.attachment.id);
            continue;
          }

          await runStructuredFileAnalysis(nextEntry.file, nextEntry.attachment.id);
        }
      };

      void Promise.all(
        Array.from({ length: Math.min(MAX_ATTACHMENT_ANALYSIS_CONCURRENCY, queue.length) }, () => worker()),
      );
    }

    if (rejectionMessages.length > 0) {
      const previewMessages = rejectionMessages.slice(0, 2).join(' ');
      const remainingCount = rejectionMessages.length - Math.min(rejectionMessages.length, 2);
      toast.error(
        remainingCount > 0
          ? `${previewMessages} ${remainingCount} additional file${remainingCount === 1 ? '' : 's'} were skipped.`
          : previewMessages,
      );
    }

    if (queuedFiles > 1) {
      toast.success(`${queuedFiles} files queued for analysis.`);
    }
  }, [isPurchaseDocument, photoAttachments, readBlobAsDataUrl, runOcrAnalysis, runStructuredFileAnalysis]);

  const handlePhotoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    void processPhotoFiles(e.target.files);
    e.target.value = '';
  }, [processPhotoFiles]);

  const handleAttachmentDragOver = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAttachmentDragActive) {
      setIsAttachmentDragActive(true);
    }
  }, [isAttachmentDragActive]);

  const handleAttachmentDragLeave = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const nextTarget = e.relatedTarget as Node | null;
    if (nextTarget && e.currentTarget.contains(nextTarget)) {
      return;
    }
    setIsAttachmentDragActive(false);
  }, []);

  const handleAttachmentDrop = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAttachmentDragActive(false);
    void processPhotoFiles(e.dataTransfer.files);
  }, [processPhotoFiles]);

  const handleRemovePhoto = useCallback(async (photoId: string) => {
    const targetAttachment = photoAttachments.find((photo) => photo.id === photoId);
    if (!targetAttachment) {
      return;
    }

    if (targetAttachment.is_persisted && targetAttachment.attachment_id) {
      try {
        const workItemId = targetAttachment.work_item_id || currentDocumentId;
        if (!workItemId) {
          throw new Error('The attachment is not linked to a saved document yet.');
        }

        await fetchAttachmentJson<{ success: boolean }>(
          `/api/v1/work-items/${workItemId}/attachments/${targetAttachment.attachment_id}`,
          {
            method: 'DELETE',
            headers: buildAuthorizedHeaders(),
          },
        );
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not remove this attachment.');
        return;
      }
    }

    setPhotoAttachments((prev) => prev.filter((photo) => photo.id !== photoId));
  }, [buildAuthorizedHeaders, currentDocumentId, fetchAttachmentJson, photoAttachments]);

  const handleRerunAttachmentAnalysis = useCallback(async (photoId: string) => {
    const attachment = photoAttachments.find((photo) => photo.id === photoId);
    if (!attachment) {
      return;
    }

    try {
      if (isImageLikeAttachment(attachment.file_name, attachment.mime_type)) {
        // Match the original intake flow: send ALL image attachments as a
        // multi-page array so total-vision can stitch overlapping receipt
        // segments together, rather than only the single selected photo.
        const imageAttachments = photoAttachments.filter((p) =>
          isImageLikeAttachment(p.file_name, p.mime_type),
        );

        // Resolve all image data URLs in parallel (same as ChildEntryForm
        // which holds all photos as base64 in state).
        const resolved = await Promise.all(
          imageAttachments.map(async (p) => ({
            id: p.id,
            dataUrl: await resolveAttachmentImageDataUrl(p),
          })),
        );

        const dataUrls = resolved.map((r) => r.dataUrl);
        const ids = resolved.map((r) => r.id);
        await runOcrAnalysisMulti(dataUrls, ids);
        return;
      }

      const file = await ensureAttachmentFile(attachment);
      await runStructuredFileAnalysis(file, attachment.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not re-run analysis for this file.');
    }
  }, [ensureAttachmentFile, photoAttachments, resolveAttachmentImageDataUrl, runOcrAnalysisMulti, runStructuredFileAnalysis]);

  const handleApplyOcrTotal = useCallback(async (photoId: string) => {
    const selectedAttachment = photoAttachments.find((photo) => photo.id === photoId);
    const ocrTotal = selectedAttachment?.sales_result?.total_amount ?? selectedAttachment?.ocr_result?.total_amount;
    if (ocrTotal == null) {
      toast.error('No detected total is available on this file yet.');
      return;
    }

    if (selectedAttachment && shouldConfirmDetectedTotalApply(selectedAttachment)) {
      const confidence = getAttachmentConfidence(selectedAttachment);
      const ok = await confirm({
        title: 'Apply detected total?',
        message: `${buildDetectedSignalLabel(confidence)}. ${selectedAttachment.ocr_result?.validation?.message || 'This detected total should be reviewed before you apply it.'} Apply it anyway?`,
      });
      if (!ok) {
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      grand_total: ocrTotal,
      balance_due: roundMoney(ocrTotal - (Number(prev.amount_paid) || 0)),
    }));
    toast.success('Detected total applied to the document.');
  }, [photoAttachments, setFormData]);

  const handleApplyOcrDate = useCallback((photoId: string) => {
    const selectedAttachment = photoAttachments.find((photo) => photo.id === photoId);
    const detectedDate = selectedAttachment?.sales_result?.requested_date
      || selectedAttachment?.ocr_result?.date
      || selectedAttachment?.structured_result?.end_date
      || selectedAttachment?.structured_result?.start_date;
    const normalizedDate = formatDateForInput(detectedDate);
    if (!normalizedDate) {
      toast.error('No usable date was detected on this file.');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      ...(selectedAttachment?.sales_result
        ? { delivery_date: normalizedDate }
        : { issued_at: normalizedDate }),
    }));
    toast.success(selectedAttachment?.sales_result
      ? 'Requested date applied to the document.'
      : (selectedAttachment?.structured_result ? 'Detected file date applied.' : 'Detected document date applied.'));
  }, [photoAttachments, setFormData, formatDateForInput]);

  const handleApplySalesCustomerPo = useCallback(async (photoId: string) => {
    const selectedAttachment = photoAttachments.find((photo) => photo.id === photoId);
    const detectedPo = selectedAttachment?.sales_result?.customer_po_number;
    if (!detectedPo) {
      toast.error('No customer PO / order reference is available on this file yet.');
      return;
    }

    const confidence = selectedAttachment ? getAttachmentConfidence(selectedAttachment) : null;
    if (selectedAttachment?.sales_result && (confidence == null || confidence < SALES_CONFIRM_SIGNAL_CONFIDENCE_THRESHOLD)) {
      const ok = await confirm({
        title: 'Apply detected customer PO?',
        message: `${buildDetectedSignalLabel(confidence)}. Review the source file before applying this customer PO if it looks uncertain. Apply it anyway?`,
      });
      if (!ok) {
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      customer_po_number: detectedPo,
    }));
    toast.success('Customer PO applied to the document.');
  }, [photoAttachments, setFormData]);

  const resetAttachmentState = useCallback(() => {
    setPhotoAttachments([]);
    setLoadingPersistedAttachments(false);
    setIsAttachmentDragActive(false);
  }, []);

  return {
    photoAttachments,
    setPhotoAttachments,
    loadingPersistedAttachments,
    setLoadingPersistedAttachments,
    isAttachmentDragActive,
    setIsAttachmentDragActive,
    ocrProcessing,
    currentDocumentId,
    buildAuthorizedHeaders,
    loadPersistedDocumentAttachments,
    syncPhotoAttachments,
    processPhotoFiles,
    handlePhotoUpload,
    handleAttachmentDragOver,
    handleAttachmentDragLeave,
    handleAttachmentDrop,
    handleRemovePhoto,
    handleRerunAttachmentAnalysis,
    handleApplyOcrTotal,
    handleApplyOcrDate,
    handleApplySalesCustomerPo,
    resetAttachmentState,
    ensureAttachmentFile,
    resolveAttachmentImageDataUrl,
  };
}
